import React, { useState } from 'react';
import { IconButton, Divider, CircularProgress } from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import InputField from '../../common/fields/InputField';
import PrimaryButton from '../../common/buttons/PrimaryButton';
import useAuth from '../../../hooks/useAuth';
import { validateLogin } from '../../../validations/loginValidation';
import { getLandingRouteForUser } from '../../../utils/auth';

export default function Login({
  onClose,
  onSwitchToRegister,
  onForgotPassword,
  onUnverifiedEmail,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateLogin(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const res = await login(formData);

      if (res.user) {
        onClose();
        navigate(getLandingRouteForUser(res.user), { replace: true });
      }
    } catch (error) {
      const data = error.response?.data;

      if (error.response?.status === 403 && data?.code === 'EMAIL_NOT_VERIFIED') {
        onUnverifiedEmail?.(formData.email);
      } else if (error.response?.status === 422 && data?.errors) {
        const mappedErrors = {};
        Object.keys(data.errors).forEach((key) => {
          mappedErrors[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key];
        });
        setErrors(mappedErrors);
      } else {
        setErrors({ submit: data?.message ?? 'An unexpected error occurred' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    if (errors[field]) {
      const { [field]: _, ...rest } = errors;
      setErrors(rest);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <h2 className="text-3xl font-bold text-light mb-2 text-center">Welcome Back</h2>
      <p className="text-gray-400 text-center mb-8">Login to your account</p>

      {errors.submit && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg">
          <p className="text-red-400 text-sm text-center">{errors.submit}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <InputField
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          error={!!errors.email}
          helperText={errors.email}
          disabled={loading}
          slotProps={{
            input: {
              startAdornment: <Email sx={{ color: '#F6911D', mr: 1 }} />,
            },
          }}
        />

        <InputField
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange('password')}
          error={!!errors.password}
          helperText={errors.password}
          disabled={loading}
          slotProps={{
            input: {
              startAdornment: <Lock sx={{ color: '#F6911D', mr: 1 }} />,
              endAdornment: (
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                  tabIndex={-1}
                  disabled={loading}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            },
          }}
        />

        <div className="text-right">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-secondary hover:text-accent text-sm font-medium transition-colors"
            disabled={loading}
          >
            Forgot Password?
          </button>
        </div>

        <PrimaryButton fullWidth type="submit" disabled={loading}>
          {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Login'}
        </PrimaryButton>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <span className="text-gray-400 text-xs sm:text-sm px-2">OR</span>
        </Divider>

        <div className="text-center">
          <p className="text-gray-400 text-xs sm:text-sm">
            Don't have an account?{' '}
            <button
              type="button"
              className="text-secondary hover:text-accent font-medium transition-colors"
              onClick={onSwitchToRegister}
              disabled={loading}
            >
              Register as Teacher
            </button>
          </p>
        </div>
      </form>
    </div>
  );
}
